// Frontend-local shared control: localization changes presentation only.
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { appLocale } from "../../locales/display";
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Calendar } from "@ui/calendar/Calendar";
import { CSSProperties } from "react";
import { TypoCaption } from "@ui/text/Typo";

const Wrapper = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	flex-direction: column;
	align-items: stretch;
	justify-content: flex-start;
`;

const InputField = styled.div<{ $size?: string }>`
	flex-grow: 1;
	box-sizing: border-box;
	overflow: hidden;
	display: grid;
	grid-template-columns: 1fr 30px;
	align-items: center;
	justify-content: space-between;
	padding: ${({ $size }) => ($size === "small" ? "0 0 0 12px" : "0 0 0 16px")};
	height: ${({ $size }) => ($size === "small" ? "36px" : "auto")};
	border: 1px solid rgba(43, 33, 79, 0.3);
	cursor: pointer;
	border-radius: 4px;
	background-color: #ffffff;
	transition: border 0.2s;
	width: 100%;
	max-width: 280px;
	font-size: ${({ $size }) => ($size === "small" ? "13px" : "0.875rem")};
	line-height: 1.25;
`;

const InputValue = styled.span`
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
`;

const IconWrapper = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 100%;
	flex-shrink: 0;
	pointer-events: none;
`;

const Modal = styled.div`
	position: absolute;
	top: 110%;
	left: 0;
	background: #fff;
	border: 1px solid #ddd;
	border-radius: 8px;
	box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
	z-index: 1000;
`;

interface DatePickerProps {
	isRange?: boolean;
	type?: "default" | "month";
	value?: Date | [Date, Date];
	onChange?: (value: Date | [Date, Date]) => void;
	locale?: string;
	title?: string;
	size?: string;
	sx?: CSSProperties;
	sxInput?: CSSProperties;
}

export const CalendarPicker: React.FC<DatePickerProps> = ({ isRange = false, value, onChange, locale = "en-US", type = "default", title, size, sx, sxInput }) => {
	useLocale();
	locale = appLocale(locale);
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const formatDate = (date: Date | null) => {
		if (!date) return "";
		if (type === "month") return date.toLocaleString(locale, { month: "long", year: "numeric" });

		const day = String(date.getDate()).padStart(2, "0");
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const year = date.getFullYear();
		return `${day}.${month}.${year}`;
	};

	const displayValue = !isRange
		? value instanceof Date
			? formatDate(value)
			: ""
		: Array.isArray(value)
		? `${formatDate(value[0])} – ${formatDate(value[1])}`
		: "";

	return (
		<Wrapper ref={ref} style={sx}>
			{title && <TypoCaption sx={{ paddingBottom: "3px" }}>{title}:</TypoCaption>}
			<InputField $size={size} style={sxInput} onClick={() => setOpen(!open)}>
				<InputValue>{displayValue || msg("sharedSelectdate")}</InputValue>
				<IconWrapper>
					<svg viewBox="0 0 24 24" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M17 9V11L12 16L7 11V9L12 14L17 9Z" fill="#000000CC" fillOpacity="1" />
					</svg>
				</IconWrapper>
			</InputField>

			{open && (
				<Modal>
					<Calendar
						isRange={isRange}
						type={type}
						value={value}
						onChange={val => {
							onChange?.(val);
							if (!isRange) setOpen(false);
							if (isRange && Array.isArray(val) && val[1]) {
								// setOpen(false);
							}
						}}
						locale={locale}
					/>
				</Modal>
			)}
		</Wrapper>
	);
};
